import React, { useState, useEffect, useRef } from 'react'
import { Row, Col } from 'reactstrap'
import { IconDots, IconStack2, IconDatabase } from '@tabler/icons-react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd'
import { removeTask } from '../../../_redux/actions/smartFlowActions'
import { useWorkspaceService } from '../../../_services/WorkspaceService'
import { aliIcon } from '../Chatbot/utils/constants'
import { useSasToken } from '../../../_hooks/useSasToken'
import { StyledTooltip } from '../Chatbot/styles'
import { setAliOpenSideBar } from '../../../_redux/actions/chatbotActions'
import { appInsights } from '../../../_services/AppInsights'
import { EventType } from '../../../utility/types'
import { useTrackEvent } from '@microsoft/applicationinsights-react-js'
import { setCurrentApp } from '../../../_redux/actions/workspaceActions'
import NewWorkspaceTab from './NewWorkspaceTab'
import WorkspaceNewTab from './WorkspaceNewTab'
import SuggestedApps from '../../_templates/SuggestedApps'
import DataMarketplaceModal from '../Teams/modals/DataMarketplaceModal'
//@ts-ignore
import { Drawer, Button } from 'dsm-react-lib'

interface Workspace {
  id: string
  name: string
  icon?: string
  [key: string]: any
}

interface WorkspaceContextType {
  currentApp: {
    workspaceId: string | null
    app: any
  } | null
  openPanel: boolean
}

interface RootState {
  workspaceReducer: {
    workspaces: {
      workspaceList: Workspace[]
      activeWorkspace: string
    }
    currentApp: {
      workspaceId: string | null
      app: any
    }
  }
  chatbotReducer: {
    openSidebar: boolean
  }
  smartFlowReducer: {
    tasks: Array<{ id: string; [key: string]: any }>
  }
}

const WorkspaceTopbar = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { appendToken } = useSasToken()
  //@ts-ignore
  const trackWSRemove = useTrackEvent(appInsights, EventType.WorkspaceRemoved)
  //@ts-ignore
  const trackAliIconClick = useTrackEvent(appInsights, EventType.AliIconClicked)

  const { workspaceList, activeWorkspace } = useSelector(
    (state: RootState) => state.workspaceReducer.workspaces
  )
  const currentApp = useSelector((state: RootState) => state.workspaceReducer.currentApp)
  const { openSidebar } = useSelector((state: RootState) => state.chatbotReducer)
  const { tasks } = useSelector((state: RootState) => state.smartFlowReducer)
  const [workspaces, setWorkspaces] = useState<Workspace[]>(workspaceList)
  const [prevWorkspaceList, setPrevWorkspaceList] = useState(workspaceList)
  // Sync workspaces from a new workspaceList reference during render, avoiding an extra effect-triggered re-render.
  if (workspaceList !== prevWorkspaceList) {
    setPrevWorkspaceList(workspaceList)
    setWorkspaces(workspaceList)
  }
  const [openPanel, setOpenPanel] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const [showOverflowMenu, setShowOverflowMenu] = useState(false)
  const [hiddenTabs, setHiddenTabs] = useState<Workspace[]>([])
  const [showDataMarketplace, setShowDataMarketplace] = useState(false)
  const tabsContainerRef = useRef<HTMLDivElement | null>(null)
  const tabsAreaRef = useRef<HTMLDivElement | null>(null)
  const overflowMenuRef = useRef<HTMLDivElement | null>(null)
  const tabRefs = useRef<(HTMLDivElement | null)[]>([])

  const { removeFromWorkspaceList, rearrangeWorkspaceList, updateActiveWorkspace } =
    useWorkspaceService()

  // Detect overflow and identify hidden tabs
  useEffect(() => {
    const checkOverflow = () => {
      if (!tabsContainerRef.current) return

      const container = tabsContainerRef.current
      const containerRect = container.getBoundingClientRect()

      // Check if tabs overflow the container (total width > visible width)
      const isOverflow = container.scrollWidth > container.clientWidth
      setIsOverflowing(isOverflow)

      if (!isOverflow) {
        setHiddenTabs([])
        return
      }

      // Define the visible area boundaries
      // visibleRightEdge: Right boundary minus 60px (space reserved for overflow button + new tab button)
      // visibleLeftEdge: Left boundary of the container
      const visibleRightEdge = containerRect.right - 60
      const visibleLeftEdge = containerRect.left
      const hidden: Workspace[] = []

      // Check each tab to see if it's fully visible
      tabRefs.current.forEach((tabElement, index) => {
        if (!tabElement || !workspaces[index]) return

        const tabRect = tabElement.getBoundingClientRect()

        // A tab should be hidden from the list and shown in dropdown if:
        // 1. Tab is completely scrolled off to the left (tabRect.right < visibleLeftEdge)
        // 2. Tab is completely scrolled off to the right (tabRect.left > visibleRightEdge)
        // 3. Tab is partially cut off on the left (starts before left edge but ends after it)
        // 4. Tab is partially cut off on the right (starts before right edge but ends after it)
        const isHidden =
          tabRect.right < visibleLeftEdge || // Completely off-screen left
          tabRect.left > visibleRightEdge || // Completely off-screen right
          (tabRect.left < visibleLeftEdge && tabRect.right > visibleLeftEdge) || // Partially cut off on left
          (tabRect.right > visibleRightEdge && tabRect.left < visibleRightEdge) // Partially cut off on right

        if (isHidden) hidden.push(workspaces[index] as Workspace)
      })

      setHiddenTabs(hidden)
    }

    checkOverflow()
    window.addEventListener('resize', checkOverflow)

    const container = tabsContainerRef.current
    if (container) {
      container.addEventListener('scroll', checkOverflow)
    }

    return () => {
      window.removeEventListener('resize', checkOverflow)
      if (container) {
        container.removeEventListener('scroll', checkOverflow)
      }
    }
  }, [workspaces])

  // Scroll active tab into view when it changes or when workspaces list changes
  useEffect(() => {
    if (!tabsContainerRef.current || !activeWorkspace) return

    const activeTabIndex = workspaces.findIndex((ws: Workspace) => ws.id === activeWorkspace)
    if (activeTabIndex !== -1 && tabRefs.current[activeTabIndex]) {
      // Use setTimeout to ensure the DOM has updated and refs are set
      setTimeout(() => {
        if (tabRefs.current[activeTabIndex]) {
          // Automatically scroll the active tab into view
          // 'nearest' means: only scroll if the tab is not already fully visible
          tabRefs.current[activeTabIndex].scrollIntoView({
            behavior: 'smooth', // Smooth animation
            inline: 'nearest', // Scroll horizontally only if needed
            block: 'nearest' // Don't scroll vertically
          })
        }
      }, 100)
    }
  }, [activeWorkspace, workspaces])

  // Close overflow menu when clicking outside
  useEffect(() => {
    if (!showOverflowMenu) return

    const handleClickOutside = (event: MouseEvent) => {
      if (overflowMenuRef.current && !overflowMenuRef.current.contains(event.target as Node)) {
        setShowOverflowMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showOverflowMenu])

  const handleTabClick = (workspace: Workspace) => {
    dispatch(setCurrentApp({ workspaceId: null, app: null })) // Clear current app when switching workspaces
    updateActiveWorkspace(workspace.id)
    setShowOverflowMenu(false)
  }

  const renderHeader = () => {
    return (
      <div className="flex flex-row justify-between items-center p-1 m-0">
        <div className="flex flex-row gap-[10px]">
          <span className="text-[var(--color-warning)] underline">Suggested apps for you</span>
        </div>
      </div>
    )
  }

  const handleOnRemoveWorkspace = async (item: Workspace) => {
    const pendingApps = workspaceList.filter((ws: Workspace) => ws.id !== item.id)
    if (tasks.some((task: any) => task.id === item.id)) {
      dispatch(removeTask({ id: item.id }))
    }
    await removeFromWorkspaceList(item)
    trackWSRemove({
      properties: {
        workspaceId: item.id,
        workspaceName: item.name
      }
    })
    if (pendingApps.length === 0) navigate(`/myspace`)
  }

  /**
   * Handle drag end event when user drops a tab
   * @param {Object} result - Contains source (where drag started) and destination (where it ended)
   * @param {Object} result.source - { index: number, droppableId: string }
   * @param {Object} result.destination - { index: number, droppableId: string } or null if dropped outside
   */
  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result

    // Ignore if:
    // 1. Dropped outside the droppable area (destination is null)
    // 2. Dropped in the same position (no reorder needed)
    if (!destination || source.index === destination.index) return

    // Reorder the workspaces array:
    // 1. Create a copy of the array (don't mutate state directly)
    // 2. Remove the dragged item from its original position
    // 3. Insert it at the new position
    const reorderedWorkspaces = Array.from(workspaces)
    const [removed] = reorderedWorkspaces.splice(source.index, 1) // Remove from old position
    reorderedWorkspaces.splice(destination.index, 0, removed) // Insert at new position

    // Update local state and persist to Redux/backend
    setWorkspaces(reorderedWorkspaces)
    rearrangeWorkspaceList(reorderedWorkspaces)
  }

  return (
    <>
      <div className="relative z-20 flex justify-start flex-row flex-nowrap flex-[0_1_auto] min-w-[calc(100%-190px)] items-center overflow-visible">
        <div
          className="flex items-center flex-1 min-w-0 max-w-full overflow-visible"
          ref={tabsAreaRef}>
          <Row className="w-100 gl-0">
            <div className="flex align-items-center !pr-0">
              {/* DragDropContext: Top-level wrapper that enables drag-and-drop */}
              {/* onDragEnd: Called when user releases the dragged item */}
              <DragDropContext onDragEnd={handleDragEnd}>
                {/* Droppable: Defines the container where tabs can be dropped */}
                {/* droppableId: Unique identifier for this drop zone */}
                {/* direction: "horizontal" for side-by-side tabs (default is vertical) */}
                <Droppable droppableId="workspace-tabs" direction="horizontal">
                  {/* Render prop pattern: (provided, snapshot) => ReactNode */}
                  {/* provided: Object with props/refs needed to make drop zone work */}
                  {/* snapshot: Object with current drag state (isDraggingOver, draggingOverWith, etc.) */}
                  {(provided, snapshot) => (
                    <div
                      className={`flex h-[40px] overflow-x-auto overflow-y-hidden items-center flex-[0_1_auto] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden scroll-smooth ${
                        snapshot.isDraggingOver ? 'bg-[rgba(0,0,0,0.02)]' : 'bg-transparent'
                      }`}
                      ref={(el) => {
                        // Set both our ref (for overflow detection) and library's ref
                        tabsContainerRef.current = el
                        provided.innerRef(el) // Required by @hello-pangea/dnd
                      }}
                      {...provided.droppableProps} // Required props (data attributes for drag tracking)
                    >
                      {workspaces && workspaces.length
                        ? workspaces.map((workspace: Workspace, index: number) => (
                            /* Draggable: Makes each tab draggable */
                            /* key: React key for list rendering */
                            /* draggableId: Unique ID for this draggable item (must be string) */
                            /* index: Position in the list (used for reordering logic) */
                            <Draggable key={workspace.id} draggableId={workspace.id} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={(el) => {
                                    // Set both our ref (for overflow detection) and library's ref
                                    tabRefs.current[index] = el
                                    provided.innerRef(el) // Required by @hello-pangea/dnd
                                  }}
                                  {...provided.draggableProps} // Required: position, data attributes
                                  {...provided.dragHandleProps} // Required: makes entire div draggable (could be on a handle instead)
                                  className={`h-full ${
                                    snapshot.isDragging
                                      ? 'opacity-80 cursor-grabbing'
                                      : 'opacity-100 cursor-grab'
                                  }`}
                                  style={{
                                    ...provided.draggableProps.style // Required: positioning during drag
                                  }}>
                                  <NewWorkspaceTab
                                    item={workspace}
                                    active={workspace.id === activeWorkspace}
                                    index={index}
                                    handleOnRemoveWorkspace={handleOnRemoveWorkspace}
                                    data-testid="iframeTabs"
                                    setCurrentApp={(app: any) => dispatch(setCurrentApp(app))}
                                    workspacesId={workspace.id}
                                  />
                                </div>
                              )}
                            </Draggable>
                          ))
                        : null}
                      {/* Placeholder: Invisible element that maintains space during drag */}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              {isOverflowing && (
                <div className="relative flex items-center" ref={overflowMenuRef}>
                  <div
                    onClick={() => setShowOverflowMenu(!showOverflowMenu)}
                    data-tooltip-id="overflowTabs"
                    className={`cursor-pointer p-[5px] mx-[5px] flex items-center justify-center transition-all duration-200 ease-in-out ${
                      showOverflowMenu
                        ? 'rounded-full bg-gray-200'
                        : 'rounded-none bg-transparent hover:rounded-full hover:bg-gray-200'
                    }`}>
                    <IconDots size={20} color="var(--text-heading)" />
                  </div>
                  <StyledTooltip id="overflowTabs" place="bottom">
                    More tabs
                  </StyledTooltip>
                  {showOverflowMenu && hiddenTabs.length > 0 && (
                    <div className="absolute top-8 right-0 bg-[var(--surface-bg)] border border-[var(--border-strong)] rounded shadow-[0_2px_8px_rgba(0,0,0,0.15)] min-w-[200px] max-h-[400px] overflow-y-auto z-30">
                      {hiddenTabs.map((workspace) => (
                        <div
                          key={workspace.id}
                          className={`px-1 py-[8px] cursor-pointer text-sm whitespace-nowrap overflow-hidden text-ellipsis transition-colors hover:bg-[var(--item-hover-bg)] ${
                            workspace.id === activeWorkspace
                              ? 'text-[var(--brand-accent-bold)] bg-[var(--workspace-overflow-menu)] font-semibold'
                              : 'text-[var(--text-body)] font-normal'
                          }`}
                          onClick={() => handleTabClick(workspace)}>
                          {workspace.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <WorkspaceNewTab setCurrentApp={(app: any) => dispatch(setCurrentApp(app))} />
            </div>
          </Row>
        </div>
        <div className="flex flex-row items-center gap-[1.5rem] ml-auto mr-[1rem] flex-shrink-0 overflow-visible py-1">
          {/* Data Marketplace button */}
          <Button
            label="Data Marketplace"
            variant="outline"
            color="neutral"
            size="small"
            icon={<IconDatabase size={16} />}
            onClick={() => setShowDataMarketplace(true)}
            data-testid="data-marketplace-btn"
            data-tooltip-id="dataMarketplace"
            className="theme-accent-outline-btn whitespace-nowrap !py-[3px]"
          />
          <StyledTooltip id="dataMarketplace" place="bottom">
            Data Marketplace
          </StyledTooltip>
          <div
            data-tooltip-id="suggestedApps"
            className={`w-5 h-5 ${
              !currentApp?.app ||
              currentApp?.app?.name === 'SmartDocs-qna' ||
              currentApp?.app?.name === 'SmartData-chartdetails'
                ? 'cursor-not-allowed'
                : 'cursor-pointer'
            }`}
            data-testid="SuggestedApps">
            <IconStack2
              size={22}
              className="help-icon focus-ring focus-ring-light"
              color={currentApp?.app ? 'var(--text-body)' : 'var(--text-muted)'}
              onClick={() => (currentApp?.app ? setOpenPanel(!openPanel) : null)}
            />
          </div>
          <StyledTooltip id="suggestedApps" place="bottom">
            Suggested apps
          </StyledTooltip>

          <img
            id="ali"
            src={appendToken(aliIcon)}
            onClick={() => {
              trackAliIconClick({ location: 'workspace' })
              dispatch(setAliOpenSideBar(!openSidebar))
            }}
            data-testid="ali"
            data-tooltip-id="ali"
            className="cursor-pointer h-7 w-7"
          />
          <StyledTooltip id="ali" place="right">
            {openSidebar ? 'Hide Centrali AI' : 'Show Centrali AI'}
          </StyledTooltip>
        </div>
      </div>
      {currentApp?.app &&
        currentApp?.app?.name !== 'SmartDocs-qna' &&
        currentApp?.app?.name !== 'SmartData-chartdetails' && (
          <Drawer
            header={renderHeader()}
            isOpen={openPanel}
            setIsOpen={() => setOpenPanel(false)}
            closeOnOutsideClick={true}
            overlay={true}
            className="suggested-apps-drawer w-[35vw]">
            <SuggestedApps setOpenPanel={setOpenPanel} application={currentApp?.app} />
          </Drawer>
        )}
      <DataMarketplaceModal
        isOpen={showDataMarketplace}
        toggle={() => setShowDataMarketplace(false)}
      />
    </>
  )
}

export default WorkspaceTopbar
